
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Globe, Loader2 } from "lucide-react";
import { useCompanyRegister } from "@/hooks/useCompanyRegister";
import { toast } from "sonner";
import ErrorBoundary from "@/components/ErrorBoundary";

export function UserCompanyRegisterSettings() {
  const { 
    providers,
    preferences, 
    updatePreferences, 
    isLoadingPreferences,
    isLoadingProviders
  } = useCompanyRegister();
  
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [apiKey, setApiKey] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Set initial values once data is loaded
  useEffect(() => {
    if (!isLoadingPreferences && preferences && !isReady) {
      setSelectedProvider(preferences.defaultProviderId || "none");
      
      if (preferences.defaultProviderId) {
        const providerKey = `${preferences.defaultProviderId}_api_key` as keyof typeof preferences;
        setApiKey(preferences[providerKey] as string || "");
      }
      
      setIsReady(true);
    }
  }, [isLoadingPreferences, preferences, isReady]);

  // Check if the selected provider requires an API key
  const requiresApiKey = React.useMemo(() => {
    if (!selectedProvider || selectedProvider === "none") return false;
    const provider = providers.find(p => p.id === selectedProvider);
    return provider?.requiresApiKey || false;
  }, [selectedProvider, providers]);

  const handleSave = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const updates: Record<string, any> = {
        defaultProviderId: selectedProvider === "none" ? null : selectedProvider
      };
      
      // Only save API key if provider requires it
      if (requiresApiKey && apiKey) {
        updates[`${selectedProvider}_api_key`] = apiKey;
      }
      
      const success = await updatePreferences(updates);
      
      if (success) {
        toast.success("Company register settings saved successfully");
      } else {
        toast.error("Failed to save company register settings");
      }
    } catch (error) {
      console.error("Error saving company register settings:", error);
      toast.error("Failed to save company register settings");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingProviders || isLoadingPreferences) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <ErrorBoundary>
      <div className="space-y-4">
        <p>
          Company registers help you quickly look up company information when adding contracts.
          Select which official company register you'd like to use.
        </p>
        
        <Card>
          <CardHeader>
            <CardTitle>Company Register Provider</CardTitle>
            <CardDescription>
              Choose which national register to use for company lookups
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="provider">Provider</Label>
                <Select 
                  value={selectedProvider} 
                  onValueChange={setSelectedProvider}
                  disabled={isSaving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None - No company lookups</SelectItem>
                    {providers.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        <div className="flex items-center">
                          {provider.name}
                          <Badge variant="outline" className="ml-2">
                            <Globe className="mr-1 h-3 w-3" />
                            {provider.countryCode}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedProvider && selectedProvider !== "none" && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {requiresApiKey 
                      ? "This provider requires an API key" 
                      : "No API key required for this provider"}
                  </p>
                )}
              </div>
              
              {requiresApiKey && (
                <div className="space-y-2">
                  <Label htmlFor="apiKey">API Key</Label>
                  <Input
                    id="apiKey"
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Enter your API key"
                    disabled={isSaving}
                  />
                  <p className="text-sm text-muted-foreground">
                    You'll need to register for an API key from the provider's website.
                  </p>
                </div>
              )}
              
              <Button 
                onClick={handleSave} 
                disabled={isSaving || (requiresApiKey && !apiKey)}
                className="mt-4"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Settings"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </ErrorBoundary>
  );
}
